import { authenticatedFetch, resolveApiUrl } from "@/lib/api";

export interface SharedChannel {
  id: string;
  owner_tenant_id: string;
  partner_tenant_id: string;
  owner_name?: string | null;
  partner_name?: string | null;
  name: string;
  status: "pending" | "active" | "disconnected" | "expired";
  expires_at: number | null;
  can_manage?: boolean;
  unread_count?: number;
}

export interface SharedChannelMember {
  user_id: string;
  tenant_id: string;
  role: string;
  display_name?: string | null;
}

export interface SharedChannelAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
}

export interface SharedChannelMessage {
  id: string;
  shared_channel_id: string;
  author_tenant_id: string;
  author_user_id: string;
  body_md: string;
  created_at: number;
  attachment?: SharedChannelAttachment | null;
}

export interface SharedChannelNotification {
  id: string;
  shared_channel_id: string;
  message_id: string;
  body: string;
  created_at: number;
  read_at: number | null;
}

export interface SharedChannelSearchHit {
  id: string;
  shared_channel_id: string;
  author_user_id: string;
  body_md: string;
  created_at: number;
  has_attachment: boolean;
  attachment: SharedChannelAttachment | null;
}

class SharedChannelApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
    this.name = "SharedChannelApiError";
  }
}

export class SharedChannelsApi {
  constructor(private readonly fetcher: typeof fetch = authenticatedFetch) {}

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const res = await this.fetcher(resolveApiUrl(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const parsed = JSON.parse(await res.text() || "null") as { error?: { code?: string; message?: string } };
    if (!res.ok) {
      throw new SharedChannelApiError(res.status, parsed?.error?.code ?? "UNKNOWN", parsed?.error?.message ?? `HTTP ${res.status}`);
    }
    return parsed as T;
  }

  list() {
    return this.request<{ channels: SharedChannel[] }>("GET", "/v1/shared-channels");
  }
  create(input: { name: string; partner_tenant_id: string; member_user_ids: string[]; expires_at: number | null }) {
    return this.request<{ channel: SharedChannel }>("POST", "/v1/shared-channels", input);
  }
  accept(id: string, member_user_ids: string[]) {
    return this.request<{ channel: SharedChannel }>("POST", `/v1/shared-channels/${id}/accept`, { member_user_ids });
  }
  disconnect(id: string) {
    return this.request<{ channel: SharedChannel }>("POST", `/v1/shared-channels/${id}/disconnect`, {});
  }
  members(id: string) {
    return this.request<{ members: SharedChannelMember[] }>("GET", `/v1/shared-channels/${id}/members`);
  }
  addMembers(id: string, user_ids: string[]) {
    return this.request<{ members: SharedChannelMember[] }>("POST", `/v1/shared-channels/${id}/members`, { user_ids });
  }
  removeMember(id: string, userId: string) {
    return this.request<{ ok: boolean }>("DELETE", `/v1/shared-channels/${id}/members/${userId}`);
  }
  messages(id: string, before?: string | null) {
    const query = before ? `?before=${encodeURIComponent(before)}` : "";
    return this.request<{ messages: SharedChannelMessage[]; next_cursor: string | null }>("GET", `/v1/shared-channels/${id}/messages${query}`);
  }
  post(id: string, body_md: string, client_message_id: string, attachment?: { filename: string; mime_type: string; bytes_base64: string } | null) {
    return this.request<{ message: SharedChannelMessage; attachment: SharedChannelAttachment | null }>("POST", `/v1/shared-channels/${id}/messages`, {
      body_md,
      client_message_id,
      ...(attachment ? { attachment } : {}),
    });
  }
  deleteMessage(id: string, messageId: string) {
    return this.request<{ ok: boolean }>("DELETE", `/v1/shared-channels/${id}/messages/${messageId}`);
  }
  search(input: { q: string; channel_id?: string; author_user_id?: string; before?: string; has_attachment?: boolean; cursor?: string }) {
    const params = new URLSearchParams();
    if (input.q) params.set("q", input.q);
    if (input.channel_id) params.set("channel_id", input.channel_id);
    if (input.author_user_id) params.set("author_user_id", input.author_user_id);
    if (input.before) params.set("before", input.before);
    if (input.has_attachment) params.set("has_attachment", "1");
    if (input.cursor) params.set("cursor", input.cursor);
    const query = params.toString();
    return this.request<{ hits: SharedChannelSearchHit[]; next_cursor: string | null }>("GET", `/v1/shared-channels/search${query ? `?${query}` : ""}`);
  }
  notifications() {
    return this.request<{ notifications: SharedChannelNotification[]; unread_count: number }>("GET", "/v1/shared-channels/notifications?unread=1");
  }
  markRead(id: string, last_read_message_id: string | null) {
    return this.request<{ ok: boolean }>("POST", `/v1/shared-channels/${id}/read`, { last_read_message_id });
  }
  downloadUrl(channelId: string, attachmentId: string) {
    return resolveApiUrl(`/v1/shared-channels/${encodeURIComponent(channelId)}/attachments/${encodeURIComponent(attachmentId)}/download`);
  }
}

export { SharedChannelApiError };
